/**
 * Registration + Authority + Token constants
 *
 * Used for the authority-check and token-link registration flow (broker/carrier).
 * Do not use these for approvalStatus on companyUsers — use approvalStatus.ts ('pending' | 'approved' | 'rejected').
 *
 * Align with backend: backend/src/config/registration.ts
 */

import { FIRESTORE_COLLECTIONS } from './firestoreCollections';

/** Result of FMCSA/SAFER authority check during registration (internal use only) */
export const REGISTRATION_AUTHORITY_RESULT = {
  PASS: 'pass',
  FAIL: 'fail'
} as const;

export type RegistrationAuthorityResult =
  (typeof REGISTRATION_AUTHORITY_RESULT)[keyof typeof REGISTRATION_AUTHORITY_RESULT];

/** Token lifetime in hours for registration continue link (e.g. 24 or 48) */
export const REGISTRATION_TOKEN_TTL_HOURS = 48;

/** Firestore collection name for registration tokens (backend writes via Admin SDK only) */
export const REGISTRATION_TOKENS_COLLECTION = 'registrationTokens';

/** Registration-related Firestore collections (same ids as backend REGISTRATION_COLLECTIONS). */
export const REGISTRATION_COLLECTIONS = {
  COMPANY_USERS: FIRESTORE_COLLECTIONS.COMPANY_USERS,
  COMPANIES: FIRESTORE_COLLECTIONS.COMPANIES,
} as const;

/** Registration flow user types; must match backend (registrationAuth) and companyUsers.userType */
export const REGISTRATION_USER_TYPE = {
  BROKER: 'broker',
  CARRIER: 'carrier'
} as const;

export type RegistrationUserType =
  (typeof REGISTRATION_USER_TYPE)[keyof typeof REGISTRATION_USER_TYPE];

/** Re-export — mirror backend/src/config/registration.ts */
export { REGISTRATION_SOURCE_REGISTER_WITH_AUTHORITY } from './adminRegistrationNotify';

/** Backend env var for FMCSA authority check. Must stay true in production Cloud Run. */
export const REGISTRATION_AUTHORITY_CHECK_ENV_VAR = 'REGISTRATION_AUTHORITY_CHECK_ENABLED' as const;

/** When unset, backend enables authority check (see backend/src/config/registration.ts). */
export const REGISTRATION_AUTHORITY_CHECK_DEFAULT_ENABLED = true as const;

/** Fallback message when backend returns 400 without a reason (authority check). Align with backend registrationAuth. */
export const REGISTRATION_AUTHORITY_FAIL_FALLBACK =
  'MC/DOT did not pass authority verification. Please verify and try again or contact Tranzit.';

/** Phase 4: Message when company name does not match FMCSA legal/DBA. Align with backend. */
export const REGISTRATION_COMPANY_NAME_MISMATCH_REASON =
  'Company name does not match FMCSA registration. Please use the legal or DBA name on file with FMCSA.';

/** Phase 2: Entity-type mismatch reasons (broker/carrier). Align with backend. */
export const REGISTRATION_ENTITY_TYPE_BROKER_REASON =
  'This MC/DOT is registered as a broker with FMCSA. Please register as a broker.';
export const REGISTRATION_ENTITY_TYPE_CARRIER_REASON =
  'This MC/DOT is registered as a carrier with FMCSA. Please register as a carrier.';

/** Broker registration: brokerage must be Active (A). Align with backend. */
export const REGISTRATION_BROKER_AUTHORITY_FAIL_REASON =
  'Broker authority is not authorized with FMCSA. Please verify your MC/DOT and brokerage authority status (must be Active).';

/** Carrier Passport only: reject when FMCSA entity type is broker or both. Align with backend. */
export const REGISTRATION_PASSPORT_ENTITY_TYPE_REASON =
  'Carrier Passport is only for motor carriers. This MC/DOT is registered as a broker (or broker and carrier) with FMCSA. Please use the main Tranzit site to register as a broker.';

/** When user supplies both DOT and MC they must match the same carrier in SAFER. Align with backend. */
export const REGISTRATION_DOT_MC_MISMATCH_REASON =
  'The DOT and MC numbers must belong to the same carrier in FMCSA. The values you entered do not match a single carrier record. Please verify both numbers.';

/** Phase 1: MC/DOT already registered. Show "already registered" message and options (login, request invite, request ownership). Align with backend. */
export const REGISTRATION_MC_DOT_ALREADY_REGISTERED_REASON =
  'This MC/DOT is already registered. Sign in if you have an account, or request an invite from your company admin, or request ownership if you are the authorized company representative.';
export const REGISTRATION_MC_DOT_ALREADY_REGISTERED_CODE = 'MC_DOT_ALREADY_REGISTERED';

/** Phase 5: Company already has a registered owner; second owner denied. Align with backend. */
export const REGISTRATION_ALREADY_HAS_OWNER_CODE = 'COMPANY_ALREADY_HAS_OWNER';
export const REGISTRATION_ALREADY_HAS_OWNER_REASON =
  'This company already has a registered owner. To join as a team member, ask the owner to add you via Add team member. For ownership or access issues, contact Tranzit.';
/** Phase 5: Company is invite-only; public registration blocked. Align with backend. */
export const REGISTRATION_INVITE_ONLY_CODE = 'REGISTRATION_INVITE_ONLY';
export const REGISTRATION_INVITE_ONLY_REASON =
  'This company allows new members only by invite. Ask your company owner to add you via Settings > Team Members.';
/** Phase 5: Registration policy values; align with backend. */
export const REGISTRATION_POLICY = {
  INVITE_ONLY: 'invite_only',
  OPEN: 'open'
} as const;
export type RegistrationPolicy = typeof REGISTRATION_POLICY[keyof typeof REGISTRATION_POLICY];
